import { Head, router } from "@inertiajs/react"
import type { ColumnDef } from "@tanstack/react-table"
import { Pencil } from "lucide-react"

import TextLink from "@/components/text-link"
import { Badge } from "@/components/ui/badge"
import { DataTable } from "@/components/ui/data-table"
import AdminLayout from "@/layouts/admin-layout"
import { formatCurrency } from "@/lib/utils"
import {
  adminAgentArchetypePath,
  adminAgentArchetypesPath,
  editAdminAgentArchetypePath,
} from "@/routes"
import type { BreadcrumbItem, Pagy } from "@/types"

interface AgentArchetype {
  uid: string
  name: string
  description: string | null
  category: string | null
  wholesale_price_cents: number
  state: string
  created_at: string
}

const columns: ColumnDef<AgentArchetype>[] = [
  {
    accessorKey: "name",
    enableSorting: true,
    header: "Name",
    cell: ({ row }) => (
      <TextLink
        href={adminAgentArchetypePath({ id: row.original.uid })}
        as="div"
        prefetch
      >
        {row.original.name}
      </TextLink>
    ),
  },
  {
    accessorKey: "wholesale_price_cents",
    enableSorting: true,
    header: "Wholesale / mo",
    cell: ({ row }) => formatCurrency(row.original.wholesale_price_cents / 100),
  },
  {
    accessorKey: "created_at",
    enableSorting: true,
    header: "Created",
    cell: ({ row }) =>
      new Date(row.original.created_at).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
  },
  {
    accessorKey: "state",
    enableSorting: true,
    header: "Status",
    cell: ({ row }) => (
      <Badge
        variant={row.original.state === "active" ? "operational" : "retired"}
        className="capitalize"
      >
        {row.original.state}
      </Badge>
    ),
  },
]

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Archetypes", href: adminAgentArchetypesPath() },
]

export default function AgentArchetypesIndex({
  agent_archetypes,
  pagy,
  sort,
}: {
  agent_archetypes: AgentArchetype[]
  pagy: Pagy
  sort: string | null
}) {
  return (
    <AdminLayout breadcrumbs={breadcrumbs}>
      <Head title="Archetypes" />
      <DataTable
        columns={columns}
        data={agent_archetypes}
        pagy={pagy}
        sort={sort}
        onSortChange={(newSort) => {
          router.visit(adminAgentArchetypesPath(), {
            data: { sort: newSort },
            preserveState: true,
            preserveScroll: true,
          })
        }}
        onPageChange={(page) => {
          router.visit(adminAgentArchetypesPath(), {
            data: { page, sort: sort ?? undefined },
            preserveState: true,
            preserveScroll: true,
          })
        }}
        actions={[
          {
            label: "Edit",
            icon: <Pencil className="size-4" />,
            onClick: (archetype: AgentArchetype) => {
              router.visit(editAdminAgentArchetypePath({ id: archetype.uid }))
            },
            variant: "default",
          },
        ]}
      />
    </AdminLayout>
  )
}
